import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileText, Package, Users, TrendingUp, Plus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalPOs: 0,
    draftPOs: 0,
    sentPOs: 0,
    totalVendors: 0
  });
  const [recentPOs, setRecentPOs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [posRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`, { headers }),
        axios.get(`${API}/vendors`, { headers })
      ]);

      const pos = posRes.data;
      setStats({
        totalPOs: pos.length,
        draftPOs: pos.filter(po => po.status === 'draft').length,
        sentPOs: pos.filter(po => po.status === 'sent').length,
        totalVendors: vendorsRes.data.length
      });

      setRecentPOs(pos.slice(0, 5));
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      setLoading(false);
    }
  };

  const statCards = [
    { label: "Total POs", value: stats.totalPOs, icon: FileText, color: "text-primary" },
    { label: "Draft", value: stats.draftPOs, icon: FileText, color: "text-muted-foreground" },
    { label: "Sent", value: stats.sentPOs, icon: TrendingUp, color: "text-primary" },
    { label: "Vendors", value: stats.totalVendors, icon: Users, color: "text-primary" }
  ];

  const getStatusBadge = (status) => {
    const variants = {
      'sent': 'bg-green-50 text-green-700 border-green-300',
      'draft': 'bg-gray-50 text-gray-700 border-gray-300',
      'completed': 'bg-blue-50 text-blue-700 border-blue-300',
      'cancelled': 'bg-red-50 text-red-700 border-red-300'
    };
    return (
      <Badge variant="outline" className={variants[status] || 'bg-gray-50'} data-testid={`status-${status}`}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Overview of your purchase order operations</p>
        </div>
        <Link to="/purchase-orders/new">
          <Button data-testid="create-po-button">
            <Plus className="w-4 h-4 mr-2" />
            New Purchase Order
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Card key={idx} data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <Icon className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent POs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg" data-testid="recent-pos-title">Recent Purchase Orders</CardTitle>
            <Link to="/purchase-orders" className="text-sm text-primary hover:underline" data-testid="view-all-pos">
              View All
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentPOs.length === 0 ? (
            <div className="text-center py-12" data-testid="no-pos-message">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-muted-foreground mb-4">No purchase orders yet</p>
              <Link to="/purchase-orders/new">
                <Button>Create Your First PO</Button>
              </Link>
            </div>
          ) : (
            <Table data-testid="recent-pos-table">
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden sm:table-cell">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentPOs.map((po) => (
                  <TableRow key={po.id} data-testid={`po-row-${po.po_number}`}>
                    <TableCell>
                      <Link to={`/purchase-orders/${po.id}`} className="font-mono text-sm text-primary hover:underline">
                        {po.po_number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm">{po.vendor_name}</TableCell>
                    <TableCell className="font-mono text-sm">₹{po.total.toFixed(2)}</TableCell>
                    <TableCell>{getStatusBadge(po.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                      {new Date(po.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
